<template>
  <div class="activity-detail-container">
    <h1 class="page-title">活动详情</h1>
    <el-card class="detail-card">
      <div class="detail-header">
        <el-button @click="handleBack" class="back-button">
          <el-icon>
            <el-icon-arrow-left />
          </el-icon>
          返回列表
        </el-button>
        <el-button type="warning" @click="handleEdit">
          编辑活动
        </el-button>
      </div>

      <div v-if="loading" class="loading-container">
        <el-skeleton :rows="10" animated />
      </div>

      <div v-else class="detail-content">
        <div class="basic-info">
          <h2 class="activity-title">{{ activityData.title }}</h2>
          <div class="activity-status">
            <el-tag :type="activityData.status === 'active' ? 'success' : 'warning'">
              {{ activityData.status === 'active' ? '活跃' : '不活跃' }}
            </el-tag>
          </div>
        </div>

        <div class="cover-image-section">
          <h3>封面图片</h3>
          <el-image
            :src="activityData.cover_image"
            fit="cover"
            style="width: 100%; max-height: 400px;"
            :preview-src-list="[activityData.cover_image]"
          />
        </div>

        <div class="carousel-images-section" v-if="activityData.carousel_images && activityData.carousel_images.length > 0">
          <h3>轮播图片</h3>
          <el-carousel height="300px" :interval="3000">
            <el-carousel-item v-for="(image, index) in activityData.carousel_images" :key="index">
              <el-image
                :src="image"
                fit="cover"
                style="width: 100%; height: 300px;"
                :preview-src-list="activityData.carousel_images"
              />
            </el-carousel-item>
          </el-carousel>
        </div>

        <div class="detail-meta">
          <div class="meta-item">
            <span class="meta-label">活动地点：</span>
            <span class="meta-value">{{ activityData.location }}</span>
          </div>
          <div class="meta-item">
            <span class="meta-label">开始时间：</span>
            <span class="meta-value">{{ activityData.start_time }}</span>
          </div>
          <div class="meta-item">
            <span class="meta-label">报名截止时间：</span>
            <span class="meta-value">{{ activityData.signup_end_time }}</span>
          </div>
          <div class="meta-item">
            <span class="meta-label">最大参与人数：</span>
            <span class="meta-value">{{ activityData.max_participants }} 人</span>
          </div>
        </div>

        <div class="description-section">
          <h3>活动描述</h3>
          <div class="description-content">{{ activityData.description }}</div>
        </div>

        <div class="details-section">
          <h3>活动详情</h3>
          <div class="details-content">{{ activityData.details }}</div>
        </div>

        <div class="registrations-info">
          <h3>报名信息</h3>
          <div class="registrations-count">
            <el-statistic title="已报名人数" :value="activityData.registrations_count" />
          </div>
          <el-button
            type="primary"
            size="large"
            @click="handleViewRegistrations"
            class="registrations-button"
          >
            查看报名列表
          </el-button>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { ElMessage } from 'element-plus';

const router = useRouter();
const route = useRoute();
const loading = ref(true);
const activityId = ref('');

const fetchActivityDetail = async () => {
  loading.value = true;
  try {
    // 模拟数据，实际项目中需要调用真实API
    ElMessage.info('获取活动详情功能正在开发中');
    loading.value = false;
  } catch (error) {
    ElMessage.error('获取活动详情失败：' + (error.message || '未知错误'));
    router.push('/manage/activities');
    loading.value = false;
  }
};

onMounted(() => {
  activityId.value = route.params.id;
  fetchActivityDetail();
});
</script>

<style scoped>
.activity-detail-container {
  padding: 20px;
}

.page-title {
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
}

.detail-card {
  max-width: 100%;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.back-button {
  margin-right: 10px;
}

.loading-container {
  padding: 20px 0;
}

.detail-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.basic-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
}

.activity-title {
  margin: 0;
  font-size: 28px;
  color: #333;
}

.activity-status {
  margin-left: 20px;
}

.cover-image-section {
  margin: 20px 0;
}

.cover-image-section h3,
.carousel-images-section h3,
.description-section h3,
.details-section h3,
.registrations-info h3 {
  margin-bottom: 10px;
  font-size: 18px;
  color: #333;
  border-left: 4px solid #409EFF;
  padding-left: 10px;
}

.carousel-images-section {
  margin: 20px 0;
}

.detail-meta {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  background-color: #f5f7fa;
  padding: 20px;
  border-radius: 8px;
  margin: 20px 0;
}

.meta-item {
  display: flex;
  align-items: center;
}

.meta-label {
  font-weight: bold;
  margin-right: 8px;
  color: #606266;
}

.meta-value {
  color: #303133;
}

.description-content,
.details-content {
  background-color: #fafafa;
  padding: 16px;
  border-radius: 4px;
  line-height: 1.6;
  white-space: pre-wrap;
  color: #303133;
}

.registrations-info {
  background-color: #ecf5ff;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #d9ecff;
  margin-top: 20px;
}

.registrations-count {
  margin-bottom: 16px;
}

.registrations-button {
  display: block;
  margin: 0 auto;
}
</style>